import React, { useState, useEffect } from 'react';
import { Clock, Users, Shield, TrendingUp, AlertTriangle, Trash2, PlusCircle, Archive } from 'lucide-react'; 

// --- FIX: Convert DD/MM/YYYY → Valid JS Date ---
const parseExpiryDate = (dateStr) => {
    if (!dateStr || !dateStr.includes("/")) return null;
    const [day, month, year] = dateStr.split("/");
    return new Date(`${year}-${month}-${day}`); // Correct format for JS: YYYY-MM-DD
};

// Function to calculate all dashboard metrics from the customer list
const calculateDashboardData = (customers) => {
    // 🔑 CORE FIX: Filter out ARCHIVED customers immediately
    const nonArchivedCustomers = customers.filter(customer => !customer.isArchived);

    if (!nonArchivedCustomers || nonArchivedCustomers.length === 0) {
        return {
            total: 0, active: 0, expired: 0, activePercentage: 0,
            valid: 0, expiring: [], currentValid: []
        };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let activeCount = 0;
    let expiredCount = 0;
    const expiringPolicies = [];
    const validPolicies = [];

    // Use the filtered list for all subsequent calculations
    nonArchivedCustomers.forEach(customer => {
        // Ensure policy number exists for unique keying
        if (!customer.policy) return;

        // --- FIXED: Use the correct parser ---
        const expiryDate = customer.expiry ? parseExpiryDate(customer.expiry) : null;

        customer.isExpired = expiryDate && expiryDate < today;

        // Check for 'Active' status
        if (customer.status === 'Active' || customer.status === 'active') {
            activeCount++;
        }

        if (customer.isExpired) {
            expiredCount++;

            // Calculate days ago expired
            const diffTime = Math.abs(today - expiryDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            expiringPolicies.push({ ...customer, days: diffDays, type: 'Expired' });
        }
        else if (expiryDate) {
            const timeDiff = expiryDate.getTime() - today.getTime();
            const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

            if (daysLeft > 0 && daysLeft <= 90) {
                // Policy is expiring soon (0 to 90 days)
                expiringPolicies.push({ ...customer, days: daysLeft, type: 'Renewal' });
            } else if (daysLeft > 90) {
                // Valid policy (more than 90 days left)
                validPolicies.push({ ...customer, days: daysLeft });
            }
        }
    });

    return {
        // Total now reflects the non-archived customers count
        total: nonArchivedCustomers.length,
        active: activeCount,
        expired: expiredCount,
        activePercentage: nonArchivedCustomers.length > 0
            ? Math.round((activeCount / nonArchivedCustomers.length) * 100)
            : 0,
        valid: validPolicies.length,
        expiring: expiringPolicies.sort((a, b) => b.days - a.days),
        currentValid: validPolicies
    };
};

// --- START: Sample Data (Updated with isArchived: false default) ---
const initialCustomers = [
    { name: "Fatima Hassan", policy: "SW-2024-000456", expiry: "28/11/2024", status: "Inactive", isArchived: false },
    { name: "Paul Nguema", policy: "SW-2024-000345", expiry: "01/01/2024", status: "Active", isArchived: false },
    { name: "Jean B. Mballa (Archived)", policy: "SW-2024-001334", expiry: "01/01/2024", status: "Active", isArchived: true }, // Set to archived for test
    { name: "short", policy: "1234567", expiry: "01/12/2026", status: "Active", isArchived: false },
    { name: "bouognong", policy: "N/A", expiry: "30/04/2026", status: "Active", isArchived: false },
    { name: "Expiring Soon", policy: "1112223", expiry: "05/12/2025", status: "Active", isArchived: false }
];
// --- END: Sample Data ---


const Dashboard = () => {

    const [customers, setCustomers] = useState(() => {
        const savedCustomers = JSON.parse(localStorage.getItem("customers_list") || "[]");
        return savedCustomers.length > 0 ? savedCustomers : initialCustomers;
    });

    const dashboardData = calculateDashboardData(customers);

    useEffect(() => {
        localStorage.setItem("customers_list", JSON.stringify(customers));
    }, [customers]);

    const updateCustomersList = (newCustomers) => {
        setCustomers(newCustomers);
    };

    // --- Action: Archive/Unarchive a Customer (toggles isArchived) ---
    const handleArchiveCustomer = (policyId) => {
        const newCustomers = customers.map(customer => 
            customer.policy === policyId 
                ? { ...customer, isArchived: !customer.isArchived } 
                : customer
        );
        updateCustomersList(newCustomers);
    };

    // --- Action: Delete/Terminate a Customer ---
    const handleDeleteCustomer = (policyId) => {
        const customerName = customers.find(c => c.policy === policyId)?.name || "this customer";
        if (window.confirm(`Are you sure you want to permanently DELETE ${customerName}? This will remove them completely.`)) {
            const newCustomers = customers.filter(customer => customer.policy !== policyId);
            updateCustomersList(newCustomers);
        }
    };

    // --- Action: Add a New Customer (for testing "increase") ---
    const handleAddSampleCustomer = () => {
        const today = new Date();
        const futureDate = new Date(today.setFullYear(today.getFullYear() + 2));
        const newDay = String(futureDate.getDate()).padStart(2, '0');
        const newMonth = String(futureDate.getMonth() + 1).padStart(2, '0');
        const newYear = futureDate.getFullYear();
        
        const newPolicy = {
            name: `New Client ${customers.length + 1}`,
            policy: `NEW-${Date.now().toString().slice(-6)}`,
            expiry: `${newDay}/${newMonth}/${newYear}`,
            status: "Active",
            isArchived: false // Default to not archived
        };

        const newCustomers = [...customers, newPolicy];
        updateCustomersList(newCustomers);
    };

    const formattedDate = new Date().toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });

    const StatCard = ({ title, value, icon: Icon, colorClass, barWidth }) => (
        <div className="bg-white rounded-xl shadow p-5 border border-gray-100 flex flex-col justify-between">
            <div>
                <div className={`text-sm font-medium ${colorClass} mb-2 flex items-center`}>
                    <Icon size={16} className="mr-1" />
                    {title}
                </div>
                <div className="text-3xl font-bold text-gray-900">{value}</div>
            </div>
            {barWidth !== undefined && (
                <div className="mt-4">
                    <p className="text-xs text-gray-500">{barWidth}% of total customers</p>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div className="h-1.5 rounded-full bg-gray-900" style={{ width: `${barWidth}%` }}></div>
                    </div>
                </div>
            )}
        </div>
    );

    const PolicyItem = ({ name, policy, days, type, onDelete, onArchive }) => {
        const isExpired = type === 'Expired';
        const isRenewal = type === 'Renewal';
        const isLongTermValid = type === 'Valid';
        
        let daysLabel;
        let itemClass;
        let chipClass;

        if (isExpired) {
            daysLabel = `Expired ${days} days ago`;
            itemClass = 'bg-red-50 text-red-700';
            chipClass = 'bg-red-600';
        } else if (isRenewal) {
            daysLabel = `${days} days left (Renewal)`;
            itemClass = 'bg-yellow-50 text-yellow-700';
            chipClass = 'bg-yellow-600';
        } else if (isLongTermValid) {
            daysLabel = `${days} days left`;
            itemClass = 'bg-green-50 text-green-700';
            chipClass = 'bg-green-600';
        } else {
            daysLabel = `N/A`;
            itemClass = 'bg-gray-50 text-gray-700';
            chipClass = 'bg-gray-500';
        }


        return (
            <div className={`flex justify-between items-center p-3 rounded-lg ${itemClass} mb-2 text-sm`}>
                <div>
                    <p className="font-semibold">{name}</p>
                    <p className="text-xs">{policy}</p>
                </div>
                <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded-full text-white text-xs font-medium ${chipClass}`}>
                        {daysLabel}
                    </span>
                    {/* Archive Button */}
                    <button 
                        onClick={() => onArchive(policy)} 
                        className="text-indigo-500 hover:text-indigo-700 p-1 rounded-full hover:bg-indigo-100 transition"
                        title={`Archive ${name}`}
                    >
                        <Archive size={16} />
                    </button>
                    {/* Delete Button */}
                    <button 
                        onClick={() => onDelete(policy)} 
                        className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-100 transition"
                        title={`Delete ${name}`}
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>
        );
    };

    const expiringAlertCount = dashboardData.expiring.filter(p => p.type === 'Expired').length;

    // Filter the lists shown in the UI to only display non-archived customers
    const nonArchivedExpiring = dashboardData.expiring.filter(p => !p.isArchived);
    const nonArchivedValid = dashboardData.currentValid.filter(p => !p.isArchived);

    return (
        <div className="p-8 bg-gray-50 min-h-[calc(100vh-64px)]">

            <div className="bg-gradient-to-r from-indigo-700 to-indigo-900 text-white rounded-xl shadow-lg p-6 flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-extrabold mb-1">Welcome back! 👋</h1>
                    <p className="text-indigo-200">Here's what's happening with your insurance operations today</p>
                </div>
                <div className="flex items-center space-x-4">
                     <button 
                        onClick={handleAddSampleCustomer}
                        className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow transition duration-150 flex items-center"
                        title="Add a new customer to test the 'increase' logic"
                    >
                        <PlusCircle size={18} className="mr-2" />
                        Add Test Customer
                    </button>
                    <div className="bg-white bg-opacity-10 p-3 rounded-lg flex items-center space-x-2">
                        <Clock size={20} />
                        <span className="font-semibold">{formattedDate}</span>
                    </div>
                </div>
            </div>

            {expiringAlertCount > 0 && (
                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md shadow-md flex justify-between items-center mb-6">
                    <div className='flex items-center'>
                        <AlertTriangle size={20} className="mr-3" />
                        <p className="font-medium">Action Required! <strong>{expiringAlertCount} policies have expired.</strong></p>
                    </div>
                    <button className="text-sm font-semibold bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 transition">
                        View Details
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
                <StatCard 
                    title="Total Customers" 
                    value={dashboardData.total} 
                    icon={Users} 
                    colorClass="text-indigo-600" 
                />
                <StatCard 
                    title="Active Policies" 
                    value={dashboardData.active} 
                    icon={Shield} 
                    colorClass="text-green-600" 
                    barWidth={dashboardData.activePercentage} 
                />
                <StatCard 
                    title="Valid Policies" 
                    value={dashboardData.valid} 
                    icon={TrendingUp} 
                    colorClass="text-blue-600" 
                />
                <StatCard 
                    title="Expiration Alerts" 
                    value={<span className="text-red-600">{dashboardData.expired} expired</span>} 
                    icon={AlertTriangle} 
                    colorClass="text-red-600" 
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                <div className="bg-white rounded-xl shadow p-6">
                    <div className="flex justify-between items-center border-b pb-3 mb-4">
                        <h2 className="text-xl font-semibold text-gray-800">Upcoming Renewals & Expired</h2>
                        <button className="text-sm text-gray-500 hover:text-gray-900 font-medium">View All</button>
                    </div>

                    {nonArchivedExpiring.length > 0 ? (
                        nonArchivedExpiring.map((policy) => (
                            <PolicyItem 
                                key={policy.policy} 
                                name={policy.name}
                                policy={policy.policy}
                                days={policy.days}
                                type={policy.type}
                                onDelete={handleDeleteCustomer} 
                                onArchive={handleArchiveCustomer}
                            />
                        ))
                    ) : (
                        <p className="text-gray-500 text-sm italic">No policies require immediate attention.</p>
                    )}
                </div>

                <div className="bg-white rounded-xl shadow p-6">
                    <div className="flex justify-between items-center border-b pb-3 mb-4">
                        <h2 className="text-xl font-semibold text-gray-800">Current Valid Policies</h2>
                        <button className="text-sm text-gray-500 hover:text-gray-900 font-medium">View All</button>
                    </div>

                    {nonArchivedValid.length > 0 ? (
                        nonArchivedValid.map((policy) => (
                            <PolicyItem
                                key={policy.policy} 
                                name={policy.name}
                                policy={policy.policy}
                                days={policy.days}
                                type={'Valid'}
                                onDelete={handleDeleteCustomer} 
                                onArchive={handleArchiveCustomer}
                            />
                        ))
                    ) : (
                        <p className="text-gray-500 text-sm italic">No long-term valid policies found.</p>
                    )}
                </div>
            </div>

        </div>
    );
};

export default Dashboard;